import { useState, useCallback } from 'react';
import { validatePassword, validateConfirmPassword } from '../../utils/registration/registrationHelpers.ts';
import { fireAnalytics } from '../../utils/firebase.js';

interface UsePasswordSetupParams {
  email: string;
  completeUserRegistration: (email: string, password: string, skipOnboarding: boolean) => Promise<any>;
  completeRegistrationLoading: boolean;
  completeRegistrationError: string | null;
}

export const usePasswordSetup = ({
  email,
  completeUserRegistration,
  completeRegistrationLoading,
  completeRegistrationError,
}: UsePasswordSetupParams) => {
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>('');
  const [skipOnboarding, setSkipOnboarding] = useState<boolean>(false);
  const [acceptTerms, setAcceptTerms] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handlePasswordSubmit = useCallback(async () => {
    const passwordValidation = validatePassword(password);
    const confirmPasswordValidation = validateConfirmPassword(password, confirmPassword);

    if (!passwordValidation.isValid) {
      setPasswordError(passwordValidation.error || '');
      return;
    }

    if (!confirmPasswordValidation.isValid) {
      setConfirmPasswordError(confirmPasswordValidation.error || '');
      return;
    }

    if (!acceptTerms) {
      return;
    }

    setIsSubmitting(true);
    setPasswordError('');
    setConfirmPasswordError('');

    try {
      fireAnalytics("registro_cuenta_creada", { category: "usuario" });
      await completeUserRegistration(email, password, skipOnboarding);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Error al completar el registro. Intenta nuevamente.';
      setPasswordError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [password, confirmPassword, acceptTerms, skipOnboarding, email, completeUserRegistration]);

  return {
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    passwordError,
    setPasswordError,
    confirmPasswordError,
    setConfirmPasswordError,
    skipOnboarding,
    setSkipOnboarding,
    acceptTerms,
    setAcceptTerms,
    isSubmitting: isSubmitting || completeRegistrationLoading,
    handlePasswordSubmit,
    errorMessage: completeRegistrationError,
  };
};
